import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/lib/permissions";
import { toast } from "sonner";
import {
  SlidersHorizontal, Factory, Plus, Trash2, FileCheck2, CloudSun,
  TrendingUp, Coins, Gauge, ShieldCheck, History, CheckCircle2, XCircle,
  PlayCircle, AlertTriangle, Loader2, Circle,
} from "lucide-react";

const SECTORS = [
  { value: "financial_services", label: "Financial Services" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "energy", label: "Energy & Utilities" },
  { value: "retail", label: "Retail & Consumer" },
  { value: "healthcare", label: "Healthcare & Life Sciences" },
  { value: "technology", label: "Technology & SaaS" },
  { value: "public_sector", label: "Public Sector" },
];

const FACTOR_ICON = { weather_seasonal: CloudSun, commodity_price: Coins, fx_volatility: TrendingUp };
const STATUS_CHIP = {
  ok: "bg-emerald-100 text-emerald-700",
  not_configured: "bg-amber-100 text-amber-700",
  error: "bg-red-100 text-red-700",
};

function Gauge100({ label, value, accent }) {
  return (
    <div className="rounded-md border border-black/5 bg-white p-4" data-testid={`gauge-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="ek-eyebrow">{label}</p>
      <p className={`mt-1 font-display text-4xl font-bold ${accent}`}>{value}</p>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-black/5">
        <div className={`h-full rounded-full ${accent.replace("text-", "bg-")}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

export default function Calibration() {
  const { user } = useAuth();
  const canWrite = hasPermission(user, "risk.write");
  const [active, setActive] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [factors, setFactors] = useState([]);
  const [showManifest, setShowManifest] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [verifyResults, setVerifyResults] = useState({});
  const [busy, setBusy] = useState(false);
  const [demoSteps, setDemoSteps] = useState([]);
  const [demoBusy, setDemoBusy] = useState(false);
  const [newFactor, setNewFactor] = useState({ label: "", description: "", value: 0.5, weight: 0.1 });
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(() => {
    api.get("/calibration/active").then(({ data }) => setActive(data)).catch(() => toast.error("Could not load calibration"));
    api.get("/calibration/factors").then(({ data }) => setFactors(data.items)).catch(() => {});
    api.get("/calibration/manifest").then(({ data }) => setManifest(data)).catch(() => {});
    api.get("/calibration/manifests").then(({ data }) => setSnapshots(data.items || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const snapshotManifest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/calibration/manifest/persist");
      toast.success(`Snapshot stored · ${data.content_hash.slice(0, 12)}…`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Snapshot failed"); }
    finally { setBusy(false); }
  };

  const verifySnapshot = async (id) => {
    try {
      const { data } = await api.post(`/calibration/manifests/${id}/verify`);
      setVerifyResults((m) => ({ ...m, [id]: data }));
      toast[data.verified ? "success" : "error"](
        data.verified ? "Replay verified — inputs reproduce the stored result" : "Verification failed");
    } catch { toast.error("Verify failed"); }
  };

  // One-click tamper-evidence demo: snapshot → verify ✓ → tamper → verify ✗ → restore → verify ✓
  const DEMO_PLAN = [
    { key: "snapshot", label: "Snapshot the live computation (hash + Ed25519 sign)" },
    { key: "verify1", label: "Replay & verify — reproduces the stored figure" },
    { key: "tamper", label: "Tamper: silently alter the stored risk index" },
    { key: "verify2", label: "Replay & verify — integrity check catches the change" },
    { key: "restore", label: "Restore the record to its true reproduced value" },
    { key: "verify3", label: "Replay & verify — integrity restored" },
  ];

  const runTamperDemo = async () => {
    setDemoBusy(true);
    const init = DEMO_PLAN.map((s) => ({ ...s, status: "pending", detail: "" }));
    setDemoSteps(init);
    const mark = (i, status, detail = "") =>
      setDemoSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, status, detail } : s)));
    try {
      mark(0, "running");
      const { data: snap } = await api.post("/calibration/manifest/persist");
      const id = snap.manifest_id;
      mark(0, "done", `${snap.content_hash.slice(0, 16)}…`);

      mark(1, "running");
      const { data: v1 } = await api.post(`/calibration/manifests/${id}/verify`);
      mark(1, v1.verified ? "pass" : "fail", `index ${v1.recomputed.calibrated_index} = stored ${v1.stored.calibrated_index}`);

      mark(2, "running");
      const { data: t } = await api.post(`/calibration/manifests/${id}/demo-tamper`);
      mark(2, "warn", `stored index ${t.original_index} → ${t.tampered_index}`);

      mark(3, "running");
      const { data: v2 } = await api.post(`/calibration/manifests/${id}/verify`);
      mark(3, v2.verified ? "fail" : "pass",
        `recomputed ${v2.recomputed.calibrated_index} ≠ tampered ${v2.stored.calibrated_index} · hash mismatch`);

      mark(4, "running");
      const { data: r } = await api.post(`/calibration/manifests/${id}/demo-restore`);
      mark(4, "done", `restored to ${r.restored_index}`);

      mark(5, "running");
      const { data: v3 } = await api.post(`/calibration/manifests/${id}/verify`);
      mark(5, v3.verified ? "pass" : "fail", `index ${v3.recomputed.calibrated_index} = stored ${v3.stored.calibrated_index}`);

      toast.success("Tamper-evidence demo complete — alteration caught, integrity restored");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Demo failed");
    } finally {
      setDemoBusy(false);
    }
  };

  const switchSector = async (sector) => {
    try { await api.put("/calibration/active", { sector }); toast.success(`Calibrated for ${sector.replace(/_/g, " ")}`); load(); }
    catch { toast.error("Switch failed"); }
  };

  const addFactor = async () => {
    if (!newFactor.label.trim()) return toast.error("Name the factor");
    try {
      await api.post("/calibration/factors", newFactor);
      toast.success("Factor added");
      setShowAdd(false); setNewFactor({ label: "", description: "", value: 0.5, weight: 0.1 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteFactor = async (id) => {
    try { await api.delete(`/calibration/factors/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };

  const c = active?.calibrated;
  const prof = active?.profile;

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Sector calibration" description="Industry-aware, deterministic risk calibration." path="/calibration" />
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-10" data-testid="calibration-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="ek-eyebrow">Engine calibration</h1>
            <h2 className="mt-1 font-display text-3xl font-bold text-ek-ink">Sector calibration</h2>
            <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">
              Industry-specific weighting and exogenous factors (weather, commodities, FX) recalibrate the posture index —
              deterministically. Every adjustment is recorded in an auditable manifest.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Factory size={15} className="text-ek-purple" />
            <select
              value={prof?.sector || "financial_services"}
              onChange={(e) => switchSector(e.target.value)}
              disabled={!canWrite}
              data-testid="sector-select"
              className="rounded-md border border-black/10 px-3 py-2 text-sm disabled:opacity-60"
            >
              {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {/* Posture comparison */}
        {c && (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="posture-compare">
            <Gauge100 label="Baseline index" value={c.baseline_index} accent="text-ek-ink/70" />
            <Gauge100 label={`Calibrated · ${prof?.name}`} value={c.calibrated_index} accent={c.calibrated_index >= 70 ? "text-red-600" : c.calibrated_index >= 50 ? "text-amber-600" : "text-emerald-600"} />
            <div className="rounded-md border border-black/5 bg-white p-4">
              <p className="ek-eyebrow flex items-center gap-1"><Gauge size={12} /> Sector delta</p>
              <p className={`mt-1 font-display text-4xl font-bold ${c.delta > 0 ? "text-red-600" : c.delta < 0 ? "text-emerald-600" : "text-ek-ink/60"}`}>
                {c.delta > 0 ? "+" : ""}{c.delta}
              </p>
              <p className="mt-2 text-xs text-ek-ink/65">vs un-calibrated baseline</p>
            </div>
          </div>
        )}

        {/* Component weights */}
        {prof && (
          <div className="mt-6 rounded-md border border-black/5 bg-white p-5" data-testid="weights-panel">
            <h3 className="ek-eyebrow flex items-center gap-1"><SlidersHorizontal size={12} /> Component weights · {prof.name}</h3>
            <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {Object.entries(prof.posture_weights).map(([k, v]) => (
                <div key={k} data-testid={`weight-${k}`}>
                  <p className="text-xs capitalize text-ek-ink/65">{k.replace(/_/g, " ").replace(" pressure", "")}</p>
                  <p className="font-display text-xl font-bold text-ek-ink">{Math.round(v * 100)}%</p>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-black/5">
                    <div className="h-full rounded-full bg-ek-purple" style={{ width: `${v * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Exogenous factors */}
        <div className="mt-8 flex items-center justify-between">
          <h3 className="ek-eyebrow flex items-center gap-1"><CloudSun size={12} /> Exogenous factors</h3>
          {canWrite && (
            <button onClick={() => setShowAdd((v) => !v)} className="ek-btn flex items-center gap-1.5" data-testid="factor-add-button">
              <Plus size={15} /> Custom factor
            </button>
          )}
        </div>

        {showAdd && canWrite && (
          <div className="mt-3 rounded-md border border-ek-purple/20 bg-white p-5" data-testid="factor-add-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <input value={newFactor.label} onChange={(e) => setNewFactor({ ...newFactor, label: e.target.value })} placeholder="Factor name (e.g. Port congestion)" data-testid="factor-label-input" className="rounded-md border border-black/10 px-3 py-2 text-sm" />
              <input value={newFactor.description} onChange={(e) => setNewFactor({ ...newFactor, description: e.target.value })} placeholder="Description (optional)" className="rounded-md border border-black/10 px-3 py-2 text-sm" />
            </div>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <label className="text-xs text-ek-ink/65">Current level: <b>{newFactor.value}</b>
                <input type="range" min="0" max="1" step="0.05" value={newFactor.value} onChange={(e) => setNewFactor({ ...newFactor, value: parseFloat(e.target.value) })} data-testid="factor-value-slider" className="mt-1 w-full" />
              </label>
              <label className="text-xs text-ek-ink/65">Weight: <b>{newFactor.weight}</b>
                <input type="range" min="0" max="0.4" step="0.01" value={newFactor.weight} onChange={(e) => setNewFactor({ ...newFactor, weight: parseFloat(e.target.value) })} data-testid="factor-weight-slider" className="mt-1 w-full" />
              </label>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={addFactor} className="ek-btn" data-testid="factor-add-submit">Add factor</button>
              <button onClick={() => setShowAdd(false)} className="ek-btn-outline">Cancel</button>
            </div>
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="exogenous-list">
          {c?.exogenous?.map((f) => {
            const Icon = FACTOR_ICON[f.key] || Coins;
            const isCustom = f.kind === "custom";
            return (
              <div key={f.key} className="rounded-md border border-black/5 bg-white p-4" data-testid={`factor-${f.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="grid h-9 w-9 place-items-center rounded-md bg-ek-purple/10 text-ek-purple"><Icon size={16} /></span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_CHIP[f.status] || STATUS_CHIP.error}`}>
                    {f.status === "not_configured" ? "Add key" : f.status}
                  </span>
                </div>
                <h4 className="mt-3 font-display text-sm font-semibold text-ek-ink">{f.label}</h4>
                <p className="text-[11px] uppercase tracking-wide text-ek-ink/65">{f.source} · weight {Math.round((f.weight || 0) * 100)}%</p>
                <p className="mt-2 font-display text-2xl font-bold text-ek-ink">{f.value != null ? f.value : "—"}</p>
                {f.place && <p className="text-xs text-ek-ink/65">{f.place}</p>}
                {f.status === "not_configured" && <p className="mt-1 text-xs text-amber-600">{f.detail}</p>}
                {isCustom && canWrite && (
                  <button onClick={() => deleteFactor(f.key)} className="ek-btn-outline mt-3 !py-1 text-xs" data-testid={`factor-delete-${f.key}`}><Trash2 size={12} /> Remove</button>
                )}
              </div>
            );
          })}
        </div>

        {/* Determinism manifest */}
        <div className="mt-8 rounded-md border border-black/5 bg-white p-5" data-testid="manifest-panel">
          <div className="flex items-center justify-between">
            <h3 className="ek-eyebrow flex items-center gap-1"><FileCheck2 size={12} /> Determinism manifest</h3>
            <button onClick={() => setShowManifest((v) => !v)} className="ek-btn-outline !py-1.5 text-xs" data-testid="manifest-toggle">
              {showManifest ? "Hide" : "Show working"}
            </button>
          </div>
          <p className="mt-1 text-sm text-ek-ink/65">
            The exact weights, FAIR seed ({manifest?.fair_quantification?.single_risk_seed}), rule version ({manifest?.rule_version}) and live factor readings behind the current score — fully reproducible & auditable.
          </p>
          {showManifest && manifest && (
            <pre className="mt-3 max-h-96 overflow-auto rounded-md bg-ek-ink/95 p-4 font-mono text-[11px] leading-relaxed text-emerald-300" data-testid="manifest-json">
              {JSON.stringify(manifest, null, 2)}
            </pre>
          )}
        </div>

        {/* Audit trail & replay — persisted, hashed, signed snapshots */}
        <div className="mt-8 rounded-md border border-black/5 bg-white p-5" data-testid="audit-replay-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="ek-eyebrow flex items-center gap-1"><ShieldCheck size={12} /> Audit trail &amp; replay</h3>
              <p className="mt-1 text-sm text-ek-ink/65">
                Snapshot the current computation into an immutable, content-hashed &amp; signed record. Any snapshot can be
                <b> replayed</b> later — recomputed purely from its stored inputs — to independently verify the figure for an auditor or regulator.
              </p>
            </div>
            {canWrite && (
              <button onClick={snapshotManifest} disabled={busy} className="ek-btn flex items-center gap-1.5 disabled:opacity-60" data-testid="manifest-snapshot-button">
                <FileCheck2 size={15} /> {busy ? "Snapshotting…" : "Snapshot current"}
              </button>
            )}
          </div>

          {/* One-click tamper-evidence demo */}
          <div className="mt-5 rounded-md border border-ek-purple/15 bg-ek-purple/[0.03] p-4" data-testid="tamper-demo">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-ek-ink/70">
                <b>30-second proof:</b> snapshot the figure, secretly tamper with the stored record, and watch the
                cryptographic replay catch it — then restore integrity.
              </p>
              {canWrite && (
                <button onClick={runTamperDemo} disabled={demoBusy}
                  className="ek-btn flex items-center gap-1.5 disabled:opacity-60" data-testid="tamper-demo-button">
                  {demoBusy ? <Loader2 size={15} className="animate-spin" /> : <PlayCircle size={15} />}
                  {demoBusy ? "Running…" : "Run tamper-evidence demo"}
                </button>
              )}
            </div>
            {demoSteps.length > 0 && (
              <ol className="mt-4 space-y-2" data-testid="tamper-demo-steps">
                {demoSteps.map((s, i) => {
                  const icon = {
                    pending: <Circle size={15} className="text-ek-ink/25" />,
                    running: <Loader2 size={15} className="animate-spin text-ek-purple" />,
                    done: <CheckCircle2 size={15} className="text-ek-ink/60" />,
                    pass: <CheckCircle2 size={15} className="text-emerald-600" />,
                    fail: <XCircle size={15} className="text-red-600" />,
                    warn: <AlertTriangle size={15} className="text-amber-600" />,
                  }[s.status];
                  return (
                    <li key={s.key} className="flex items-start gap-2.5 text-sm" data-testid={`tamper-demo-step-${s.key}`}>
                      <span className="mt-0.5 shrink-0">{icon}</span>
                      <span className="flex-1">
                        <span className={s.status === "pending" ? "text-ek-ink/65" : "text-ek-ink/80"}>{s.label}</span>
                        {s.detail && <span className="ml-2 font-mono text-[11px] text-ek-ink/65">{s.detail}</span>}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          {snapshots.length === 0 ? (
            <p className="mt-4 flex items-center gap-1.5 text-sm text-ek-ink/65" data-testid="snapshots-empty">
              <History size={14} /> No snapshots yet{canWrite ? " — capture one to start the audit trail." : "."}
            </p>
          ) : (
            <div className="mt-4 overflow-hidden rounded-md border border-black/5" data-testid="snapshots-list">
              <table className="w-full text-left text-sm">
                <thead className="bg-ek-ink/[0.03] text-[11px] uppercase tracking-wide text-ek-ink/65">
                  <tr>
                    <th className="px-3 py-2">Captured</th>
                    <th className="px-3 py-2">Index</th>
                    <th className="px-3 py-2">Content hash</th>
                    <th className="px-3 py-2 text-right">Replay</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshots.map((s) => {
                    const v = verifyResults[s.manifest_id];
                    return (
                      <tr key={s.manifest_id} className="border-t border-black/5" data-testid={`snapshot-row-${s.manifest_id}`}>
                        <td className="px-3 py-2 text-ek-ink/70">{new Date(s.created_at).toLocaleString()}</td>
                        <td className="px-3 py-2 font-display font-bold text-ek-ink">{s.manifest?.posture?.calibrated_index ?? "—"}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-ek-ink/65">{s.content_hash?.slice(0, 16)}…</td>
                        <td className="px-3 py-2 text-right">
                          {v ? (
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${v.verified ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`verify-result-${s.manifest_id}`}>
                              {v.verified ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                              {v.verified ? "Verified" : "Failed"}
                            </span>
                          ) : (
                            <button onClick={() => verifySnapshot(s.manifest_id)} className="ek-btn-outline !py-1 text-xs" data-testid={`verify-button-${s.manifest_id}`}>Verify</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
